import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Autocomplete,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Switch,
  FormControlLabel,
  CircularProgress,
  Snackbar,
  Alert,
  MenuItem,
  Paper,
  IconButton,
  Radio,
  RadioGroup,
  Skeleton,
} from "@mui/material";
import {
  Add as AddIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  Upload as UploadIcon,
  InsertDriveFile as PdfIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import { DatePicker } from "antd";
import axios from "axios";
import { BASE_URL } from "../../../services/configUrls";
import dayjs from "dayjs";

// Import Components
import FeaturesModal from "./FeaturesModal";
import AddFeatureModal from "./AddFeatureModal";

const FieldLabel = ({ children, required = false, width = "140px" }) => (
  <Box
    sx={{
      minWidth: width,
      maxWidth: width,
      display: "flex",
      alignItems: "center",
      fontSize: "13px",
      fontWeight: 400,
      color: "#4B5563",
      pr: 2,
    }}
  >
    {children}
    {required && <span style={{ color: "#E85858", marginLeft: "2px" }}>*</span>}
  </Box>
);

const FormField = ({ children, sx = {} }) => (
  <Box sx={{ mb: 1.5, display: "flex", alignItems: "flex-start", ...sx }}>
    {children}
  </Box>
);

const MembershipDetails = ({ selectedInstitute, onSaveSuccess }) => {
  const [membershipMode, setMembershipMode] = useState("new");
  const [renewalDetails, setRenewalDetails] = useState(null);
  const [loadingRenewalDetails, setLoadingRenewalDetails] = useState(false);
  const [isEligibleForRenewal, setIsEligibleForRenewal] = useState(false);

  const [membershipTypes, setMembershipTypes] = useState([]);
  const [loadingMembershipTypes, setLoadingMembershipTypes] = useState(false);
  const [membershipType, setMembershipType] = useState(null);
  const [membershipNo, setMembershipNo] = useState("");
  const [signupMonthYear, setSignupMonthYear] = useState(null);
  const [validTillMonthYear, setValidTillMonthYear] = useState(null);
  const [renewalMonthYear, setRenewalMonthYear] = useState(null);
  const [invoiceDate, setInvoiceDate] = useState(null);
  const [invoiceDocument, setInvoiceDocument] = useState(null);
  const [invoiceAmount, setInvoiceAmount] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [openMembershipDialog, setOpenMembershipDialog] = useState(false);
  const [newMembershipType, setNewMembershipType] = useState("");
  const [newMembershipValidityYears, setNewMembershipValidityYears] =
    useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCreatingMembership, setIsCreatingMembership] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  //const [renewalActivation, setRenewalActivation] = useState("after_expiry");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [isEditingValidityYears, setIsEditingValidityYears] = useState(false);
  const [validityYears, setValidityYears] = useState(null);
  const [originalValidityYears, setOriginalValidityYears] = useState(null);

  //RM Details
  const [rmName, setRmName] = useState("");
  const [rmEmail, setRmEmail] = useState("");
  const [rmMobile, setRmMobile] = useState("");

  const [featuresModal, setFeaturesModal] = useState({
    open: false,
    features: [],
    membershipName: "",
    membershipId: null,
  });

  const [addFeatureModal, setAddFeatureModal] = useState({
    open: false,
    membershipId: null,
  });

  useEffect(() => {
    if (selectedInstitute?.institue_id) {
      fetchRenewalDetails();
    }
  }, [selectedInstitute]);

  useEffect(() => {
    fetchMembershipTypes();
  }, []);

  useEffect(() => {
    if (
      membershipType &&
      membershipType !== "Create New" &&
      membershipType.validity_years
    ) {
      const years = parseInt(membershipType.validity_years);
      setValidityYears(years);
      setOriginalValidityYears(years);
    } else {
      setValidityYears(null);
      setOriginalValidityYears(null);
    }
    setIsEditingValidityYears(false);
  }, [membershipType]);

  useEffect(() => {
    if (
      signupMonthYear &&
      validityYears &&
      Number(validityYears) > 0 &&
      membershipMode === "new"
    ) {
      const calculatedValidTill = dayjs(signupMonthYear).add(
        Number(validityYears),
        "year"
      );
      setValidTillMonthYear(calculatedValidTill);
    }
  }, [signupMonthYear, validityYears, membershipMode]);

  // Auto-calculate valid till based on renewal date (1 month after)
  useEffect(() => {
    if (membershipMode === "renewal" && renewalMonthYear) {
      const calculatedValidTill = dayjs(renewalMonthYear).add(1, "month");
      setValidTillMonthYear(calculatedValidTill);
    }
  }, [renewalMonthYear, membershipMode]);

  const fetchRenewalDetails = async () => {
    setLoadingRenewalDetails(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/accounts/institute-membership/renewal-details/${selectedInstitute.institue_id}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      const data = response.data;
      setRenewalDetails(data);
      setIsEligibleForRenewal(data.is_eligible_for_renewal);

      if (data.is_eligible_for_renewal) {
        setMembershipMode("renewal");
        populateRenewalFields(data);
      } else {
        setMembershipMode("new");
      }
    } catch (error) {
      console.error("Error fetching renewal details:", error);
      setIsEligibleForRenewal(false);
      setMembershipMode("new");
    } finally {
      setLoadingRenewalDetails(false);
    }
  };

  const populateRenewalFields = (data) => {
    const membership = membershipTypes.find(
      (m) => m.membership_id === data.membership_id
    );
    if (membership) {
      setMembershipType(membership);
    }

    setMembershipNo(data.membership_no || "");

    if (data.valid_from_month_year) {
      setSignupMonthYear(dayjs(data.valid_from_month_year, "YYYY-MM"));
    }

    if (data.valid_till_month_year) {
      setValidTillMonthYear(dayjs(data.valid_till_month_year, "YYYY-MM"));
      setRenewalMonthYear(
        dayjs(data.valid_till_month_year, "YYYY-MM").subtract(1, "month")
      );
    }

    // Don't populate invoice amount and invoice no in renewal mode - keep them empty
    setInvoiceAmount("");
    setInvoiceNo("");
    setIsActive(data.is_active ?? true);

    setValidityYears(data.validity || null);
    setOriginalValidityYears(data.validity || null);

    // Auto-populate RM details from backend if available
    setRmName(data.rm_name || "");
    setRmEmail(data.rm_email || "");
    setRmMobile(data.rm_mobile || "");
  };

  useEffect(() => {
    if (
      membershipTypes.length > 0 &&
      renewalDetails &&
      membershipMode === "renewal"
    ) {
      const membership = membershipTypes.find(
        (m) => m.membership_id === renewalDetails.membership_id
      );
      if (membership && !membershipType) {
        setMembershipType(membership);
      }
    }
  }, [membershipTypes, renewalDetails, membershipMode]);

  const fetchMembershipTypes = async () => {
    setLoadingMembershipTypes(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/accounts/memberships-with-features`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      const memberships = response.data?.data || response.data || [];
      setMembershipTypes(memberships);
    } catch (error) {
      console.error("Error fetching membership types:", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch membership types",
        severity: "error",
      });
    } finally {
      setLoadingMembershipTypes(false);
    }
  };

  const handleMembershipModeChange = (event) => {
    const newMode = event.target.value;
    setMembershipMode(newMode);
    // if (newMode === "renewal") {
    //   // Expired: default "now", Not expired: default "after_expiry"
    //   setRenewalActivation(isExpired ? "now" : "after_expiry");
    // } else {
    //   setRenewalActivation("");
    // }
    // if (newMode === "renewal" && renewalDetails) {
    //   populateRenewalFields(renewalDetails);
    // } else if (newMode === "new") {
    //   resetForm();
    // }
  };

  const handleMembershipTypeChange = (event, newValue) => {
    if (newValue === "Create New") {
      setOpenMembershipDialog(true);
      return;
    }
    setMembershipType(newValue);
    // 🔥 AUTO OPEN FEATURES MODAL
   if (newValue && newValue.features) {
    setFeaturesModal({
      open: true,
      features: newValue.features || [],
      membershipName: newValue.membership_name,
      membershipId: newValue.membership_id,
    });
  }
  };

  const handleCreateNewMembershipType = async () => {
    if (!newMembershipType.trim()) {
      return;
    }

    setIsCreatingMembership(true);
    try {
      const response = await axios.post(
        `${BASE_URL}/accounts/create/membership`,
        {
          membership_name: newMembershipType.trim(),
          validity_years: newMembershipValidityYears,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      setSnackbar({
        open: true,
        message:
          response?.data?.message || "Membership type created successfully",
        severity: "success",
      });

      await fetchMembershipTypes();

      const newMembership = response.data?.membership || {
        membership_id: response.data?.membership_id,
        membership_name: newMembershipType.trim(),
        validity_years: `${newMembershipValidityYears} ${
          newMembershipValidityYears === 1 ? "Year" : "Years"
        }`,
        features: [],
      };
      setMembershipType(newMembership);

      setNewMembershipType("");
      setNewMembershipValidityYears(1);
      setOpenMembershipDialog(false);
    } catch (error) {
      console.error("Error creating membership type:", error);
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          "Failed to create membership type. Please try again.",
        severity: "error",
      });
    } finally {
      setIsCreatingMembership(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      if (file.type !== "application/pdf") {
        setSnackbar({
          open: true,
          message: "Only PDF files are allowed",
          severity: "error",
        });
        event.target.value = null;
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        setSnackbar({
          open: true,
          message: "File size must be less than 10MB",
          severity: "error",
        });
        event.target.value = null;
        return;
      }

      setInvoiceDocument(file);
    }
  };

  const handleRemoveFile = () => {
    setInvoiceDocument(null);
    const fileInput = document.getElementById("invoice-upload");
    if (fileInput) {
      fileInput.value = null;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const handleConfirmValidityYears = () => {
    if (validityYears) {
      setIsEditingValidityYears(false);
      setSnackbar({
        open: true,
        message: `Validity years updated to ${validityYears} ${
          validityYears === 1 ? "year" : "years"
        }`,
        severity: "success",
      });
    }
  };

  // const validateForm = () => {
  //   if (!membershipType) {
  //     setSnackbar({
  //       open: true,
  //       message: "Please select a membership plan",
  //       severity: "error",
  //     });
  //     return false;
  //   }
  //   if (!membershipNo.trim()) {
  //     setSnackbar({
  //       open: true,
  //       message: "Please enter membership number",
  //       severity: "error",
  //     });
  //     return false;
  //   }
  //   if (!signupMonthYear) {
  //     setSnackbar({
  //       open: true,
  //       message: "Please select signup month and year",
  //       severity: "error",
  //     });
  //     return false;
  //   }
  //   if (!validTillMonthYear) {
  //     setSnackbar({
  //       open: true,
  //       message: "Please select valid till month and year",
  //       severity: "error",
  //     });
  //     return false;
  //   }
  //   if (membershipMode === "renewal" && !renewalMonthYear) {
  //     setSnackbar({
  //       open: true,
  //       message: "Please select renewal month and year",
  //       severity: "error",
  //     });
  //     return false;
  //   }
  //   if (!validityYears) {
  //     setSnackbar({
  //       open: true,
  //       message: "Please select validity years",
  //       severity: "error",
  //     });
  //     return false;
  //   }
  //   if (!invoiceAmount.trim()) {
  //     setSnackbar({
  //       open: true,
  //       message: "Please enter invoice amount",
  //       severity: "error",
  //     });
  //     return false;
  //   }
  //   return true;
  // };

  const validateForm = () => {
  if (!membershipType) {
    setSnackbar({
      open: true,
      message: "Please select a membership plan",
      severity: "error",
    });
    return false;
  }

  // ✅ Check features count
  if (
    membershipType &&
    membershipType !== "Create New" &&
    (!membershipType.features || membershipType.features.length === 0)
  ) {
    setSnackbar({
      open: true,
      message: "Please add at least one feature to the membership plan",
      severity: "error",
    });
    return false;
  }

  if (!membershipNo.trim()) {
    setSnackbar({
      open: true,
      message: "Please enter membership number",
      severity: "error",
    });
    return false;
  }

  if (!signupMonthYear) {
    setSnackbar({
      open: true,
      message: "Please select signup month and year",
      severity: "error",
    });
    return false;
  }

  if (!validTillMonthYear) {
    setSnackbar({
      open: true,
      message: "Please select valid till month and year",
      severity: "error",
    });
    return false;
  }

  if (membershipMode === "renewal" && !renewalMonthYear) {
    setSnackbar({
      open: true,
      message: "Please select renewal month and year",
      severity: "error",
    });
    return false;
  }

  if (!validityYears) {
    setSnackbar({
      open: true,
      message: "Please select validity years",
      severity: "error",
    });
    return false;
  }

  if (!invoiceAmount.trim()) {
    setSnackbar({
      open: true,
      message: "Please enter invoice amount",
      severity: "error",
    });
    return false;
  }

  return true;
};
  const resetForm = () => {
    setMembershipType(null);
    setMembershipNo("");
    setSignupMonthYear(null);
    setValidTillMonthYear(null);
    setRenewalMonthYear(null);
    setInvoiceDate(null);
    setValidityYears(null);
    setOriginalValidityYears(null);
    setInvoiceDocument(null);
    setInvoiceAmount("");
    setInvoiceNo("");
    setIsActive(true);
    setAttemptedSubmit(false);
    setIsEditingValidityYears(false);
    setRmName("");
    setRmEmail("");
    setRmMobile("");

    const fileInput = document.getElementById("invoice-upload");
    if (fileInput) {
      fileInput.value = null;
    }
  };

  const handleSaveMembership = async () => {
    setAttemptedSubmit(true);

    if (!validateForm()) {
      return;
    }
    
    // RM Details validation
    if (
      membershipType &&
      ![1, 2, 9, 13].includes(Number(membershipType.membership_id))
    ) {

      // RM Name
      if (!rmName.trim()) {
        setSnackbar({
          open: true,
          message: "Please enter RM name",
          severity: "error",
        });
        return;
      }

      // RM Email
      if (!rmEmail.trim()) {
        setSnackbar({
          open: true,
          message: "Please enter RM email",
          severity: "error",
        });
        return;
      }

      // Email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (!emailRegex.test(rmEmail)) {
        setSnackbar({
          open: true,
          message: "Please enter valid RM email",
          severity: "error",
        });
        return;
      }

      // RM Mobile
      if (!rmMobile.trim()) {
        setSnackbar({
          open: true,
          message: "Please enter RM mobile number",
          severity: "error",
        });
        return;
      }

      // Mobile validation
      const mobileRegex = /^[0-9]{10}$/;

      if (!mobileRegex.test(rmMobile)) {
        setSnackbar({
          open: true,
          message: "Please enter valid 10 digit mobile number",
          severity: "error",
        });
        return;
      }
    }
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("institute_id", selectedInstitute.institue_id);
      formData.append("membership_id", membershipType.membership_id);
      formData.append("membership_no", membershipNo);
      formData.append("signup_month_year", signupMonthYear.format("YYYY-MM"));
      formData.append(
        "valid_till_month_year",
        validTillMonthYear.format("YYYY-MM")
      );

      if (membershipMode === "renewal") {
        formData.append(
          "renewal_month_year",
          renewalMonthYear.format("YYYY-MM")
        );
        // Send renewal activation selection ("now" or "after_expiry")
        formData.append("renewal_activation", isExpired ? "now" : "after_expiry");
        // Always send active as true for renewal
        formData.append("is_active", true);
      } else {
        // For new membership, send the status
        formData.append("is_active", isActive);
      }

      // Make invoice date optional
      if (invoiceDate) {
        formData.append("invoice_date", invoiceDate.format("YYYY-MM-DD"));
      }

      formData.append("validity_years", validityYears);

      // Make invoice document optional
      if (invoiceDocument) {
        formData.append("invoice_document", invoiceDocument);
      }

      formData.append("invoice_amount", invoiceAmount);

      // Make invoice number optional
      if (invoiceNo.trim()) {
        formData.append("invoice_no", invoiceNo);
      }

      // RM Details - send if available, convert to string
      if (rmName.trim()) {
        formData.append("rm_name", rmName.toString().trim());
      }
      if (rmEmail.trim()) {
        formData.append("rm_email", rmEmail.toString().trim());
      }
      if (rmMobile.trim()) {
        formData.append("rm_mobile", rmMobile.toString().trim());
      }

      const endpoint =
        membershipMode === "renewal"
          ? `${BASE_URL}/accounts/institute-membership/renew`
          : `${BASE_URL}/accounts/institute-membership`;

      const response = await axios.post(endpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });

      setSnackbar({
        open: true,
        message:
          response?.data?.message ||
          `Membership ${
            membershipMode === "renewal" ? "renewed" : "saved"
          } successfully`,
        severity: "success",
      });

      resetForm();

      if (onSaveSuccess) {
        onSaveSuccess();
      }

      if (selectedInstitute?.institue_id) {
        await fetchRenewalDetails();
      }
    } catch (error) {
      console.error("Error saving membership:", error);
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.detail ||
          error?.response?.data?.detail?.[0]?.msg ||
          `Failed to ${
            membershipMode === "renewal" ? "renew" : "save"
          } membership. Please try again.`,
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewFeatures = () => {
    if (!membershipType) return;

    setFeaturesModal({
      open: true,
      features: membershipType.features || [],
      membershipName: membershipType.membership_name,
      membershipId: membershipType.membership_id,
    });
  };

  const handleCloseFeaturesModal = () => {
    setFeaturesModal((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const handleOpenAddFeatureModal = (membershipId) => {
    setAddFeatureModal({
      open: true,
      membershipId: membershipId,
    });
  };

  const handleCloseAddFeatureModal = () => {
    setAddFeatureModal({
      open: false,
      membershipId: null,
    });
  };

  const handleFeaturesUpdated = async () => {
    await fetchMembershipTypes();

    setTimeout(() => {
      setMembershipTypes((prevTypes) => {
        const updatedMembership = prevTypes.find(
          (m) => m.membership_id === featuresModal.membershipId
        );

        if (updatedMembership) {
          if (
            membershipType?.membership_id === updatedMembership.membership_id
          ) {
            setMembershipType(updatedMembership);
          }

          setFeaturesModal((prev) => ({
            ...prev,
            features: updatedMembership.features || [],
          }));
        }

        return prevTypes;
      });
    }, 100);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  // Renewal Activation — check if membership has expired
  // validTill < today  → isExpired = true  → "Now" default+enabled, "After Expiry" disabled
  // validTill >= today → isExpired = false → "After Expiry" default+enabled, "Now" enabled
  const isExpired =
    validTillMonthYear &&
    dayjs(validTillMonthYear)
      .endOf("month")
      .isBefore(dayjs());

  // Auto-set correct default when isExpired or membershipMode changes
  useEffect(() => {
    if (membershipMode === "renewal") {
      if (isExpired) {
        // Expired: "Now" is default, "After Expiry" is disabled
        //setRenewalActivation("now");
      } else {
        // Not expired: "After Expiry" is default, both enabled
        //setRenewalActivation("after_expiry");
      }
    }
  }, [isExpired, membershipMode]);

  // SKELETON LOADING COMPONENT
  if (loadingRenewalDetails) {
    return (
      <>
        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 3 }}>
          <Skeleton variant="text" width={180} height={32} />
        </Box>

        {/* Membership Type Radio Skeleton */}
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
          <Skeleton variant="text" width={140} height={24} />
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton variant="text" width={60} height={24} />
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton variant="text" width={80} height={24} />
        </Box>

        {/* Form Fields Skeletons */}
        {[...Array(8)].map((_, index) => (
          <Box
            key={index}
            sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}
          >
            <Skeleton variant="text" width={140} height={24} />
            <Skeleton
              variant="rectangular"
              width={400}
              height={40}
              sx={{ borderRadius: 1 }}
            />
          </Box>
        ))}

        {/* Upload Skeleton */}
        <Box sx={{ mb: 2, display: "flex", alignItems: "flex-start", gap: 2 }}>
          <Skeleton variant="text" width={140} height={24} />
          <Skeleton
            variant="rectangular"
            width={500}
            height={120}
            sx={{ borderRadius: 1 }}
          />
        </Box>

        {/* Button Skeleton */}
        <Box sx={{ mt: 3 }}>
          <Skeleton
            variant="rectangular"
            width={180}
            height={40}
            sx={{ borderRadius: 1 }}
          />
        </Box>
      </>
    );
  }

  return (
    <>
      <Divider sx={{ my: 4 }} />

      <Typography
        variant="h6"
        sx={{
          fontSize: "16px",
          fontWeight: 600,
          color: "#1F1F1F",
          mb: 3,
        }}
      >
        Membership Details
      </Typography>

      {/* Membership Type Selection - Conditional Display */}
      {isEligibleForRenewal ? (
        <FormField>
          <FieldLabel required>Membership Type</FieldLabel>
          <RadioGroup
            row
            value={membershipMode}
            onChange={handleMembershipModeChange}
          >
            <FormControlLabel
              value="renewal"
              control={<Radio />}
              label="Renewal"
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontSize: "14px",
                  color: "#1F2937",
                },
              }}
            />
          </RadioGroup>
        </FormField>
      ) : (
        <FormField>
          <FieldLabel required>Membership Type</FieldLabel>
          <RadioGroup
            row
            value={membershipMode}
            onChange={handleMembershipModeChange}
          >
            <FormControlLabel
              value="new"
              control={<Radio />}
              label="New"
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontSize: "14px",
                  color: "#1F2937",
                },
              }}
            />
          </RadioGroup>
        </FormField>
      )}

      {/* Membership Plan Selection */}
      <FormField>
        <FieldLabel required>Membership Plan</FieldLabel>
        <Box sx={{ flex: 1, maxWidth: "400px", display: "flex", gap: 1 }}>
          <Autocomplete
            value={membershipType}
            onChange={handleMembershipTypeChange}
            options={["Create New", ...membershipTypes]}
            getOptionLabel={(option) => {
              if (option === "Create New") return "Create New Plan";
              return option?.membership_name || "";
            }}
            loading={loadingMembershipTypes}
            sx={{ flex: 1 }}
            renderOption={(props, option) => {
              if (option === "Create New") {
                return (
                  <Box
                    {...props}
                    component="li"
                    sx={{
                      color: "#3B82F6 !important",
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      borderBottom: "1px solid #E5E7EB",
                      mb: 0.5,
                      pb: 1,
                    }}
                  >
                    <AddIcon sx={{ fontSize: 16 }} />
                    Create New Plan
                  </Box>
                );
              }
              return (
                <Box {...props} component="li">
                  {option.membership_name}
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select Membership Plan"
                size="small"
                error={attemptedSubmit && !membershipType}
                helperText={
                  attemptedSubmit && !membershipType
                    ? "Membership plan is required"
                    : ""
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingMembershipTypes ? (
                        <CircularProgress color="inherit" size={16} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          {membershipType && membershipType !== "Create New" && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
              onClick={handleViewFeatures}
              sx={{
                fontSize: "12px",
                padding: "4px 12px",
                whiteSpace: "nowrap",
                borderColor:
                  membershipType.features?.length === 0 ? "#EF4444" : "#3B82F6",
                color:
                  membershipType.features?.length === 0 ? "#EF4444" : "#3B82F6",
                "&:hover": {
                  borderColor:
                    membershipType.features?.length === 0 ? "#DC2626" : "#2563EB",
                  backgroundColor:
                    membershipType.features?.length === 0
                      ? "#FEF2F2"
                      : "#EFF6FF",
                },
              }}
            >
              View ({membershipType.features?.length || 0})
            </Button>
          )}
        </Box>
      </FormField>

      {/* Validity Years - Non-editable in renewal mode */}
      {membershipType && membershipType !== "Create New" && (
        <FormField>
          <FieldLabel required>Validity</FieldLabel>
          <Box
            sx={{
              flex: 1,
              maxWidth: "400px",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {!isEditingValidityYears || membershipMode === "renewal" ? (
              <>
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    backgroundColor: validityYears ? "#F3F4F6" : "#FEF3F2",
                    borderRadius: 1,
                    border:
                      attemptedSubmit && !validityYears
                        ? "1px solid #E85858"
                        : "1px solid #E5E7EB",
                    flex: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: validityYears ? "#1F2937" : "#E85858",
                    }}
                  >
                    {validityYears
                      ? `${validityYears} ${
                          validityYears === 1 ? "Year" : "Years"
                        } Membership`
                      : "Click edit to set validity years"}
                  </Typography>
                </Box>
              </>
            ) : (
              <>
                <TextField
                  select
                  value={validityYears || ""}
                  onChange={(e) => setValidityYears(e.target.value)}
                  size="small"
                  sx={{ flex: 1 }}
                >
                  {[...Array(10)].map((_, index) => {
                    const year = index + 1;
                    return (
                      <MenuItem key={year} value={year}>
                        {year} {year === 1 ? "year" : "years"} membership
                      </MenuItem>
                    );
                  })}
                </TextField>
                <IconButton
                  size="small"
                  onClick={handleConfirmValidityYears}
                  sx={{
                    color: "#10B981",
                    "&:hover": {
                      backgroundColor: "#ECFDF5",
                    },
                  }}
                >
                  <CheckIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => {
                    const years = membershipType
                      ? parseInt(membershipType.validity_years)
                      : originalValidityYears;
                    setValidityYears(years);
                    setIsEditingValidityYears(false);
                  }}
                  sx={{
                    color: "#EF4444",
                    "&:hover": {
                      backgroundColor: "#FEF2F2",
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </>
            )}
          </Box>
          {attemptedSubmit && !validityYears && (
            <Typography
              sx={{ color: "#E85858", fontSize: "12px", mt: 0.5, ml: "156px" }}
            >
              Validity is required
            </Typography>
          )}
        </FormField>
      )}

      <FormField>
        <FieldLabel required>Membership No.</FieldLabel>
        <TextField
          placeholder="Enter membership number"
          value={membershipNo}
          onChange={(e) => setMembershipNo(e.target.value)}
          size="small"
          sx={{ flex: 1, maxWidth: "400px" }}
          autoComplete="off"
          error={attemptedSubmit && !membershipNo.trim()}
          helperText={
            attemptedSubmit && !membershipNo.trim()
              ? "Membership number is required"
              : ""
          }
        />
      </FormField>

      <FormField>
        <FieldLabel required>Signup</FieldLabel>
        <Box sx={{ flex: 1, maxWidth: "200px" }}>
          <DatePicker
            value={signupMonthYear}
            onChange={(date) => setSignupMonthYear(date)}
            picker="month"
            format="MM/YYYY"
            placeholder="MM/YYYY"
            disabled={membershipMode === "renewal"}
            style={{ width: "100%" }}
            status={attemptedSubmit && !signupMonthYear ? "error" : ""}
          />
          {attemptedSubmit && !signupMonthYear && (
            <Typography sx={{ color: "#E85858", fontSize: "12px", mt: 0.5 }}>
              Signup month and year is required
            </Typography>
          )}
        </Box>
      </FormField>

      {/* Renewal and Valid Till in same row */}
      {membershipMode === "renewal" ? (
        <Box sx={{ mb: 2.5, display: "flex", gap: 3 }}>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "flex-start",
            }}
          >
            <FieldLabel required width="140px">
              Renewal
            </FieldLabel>
            <Box sx={{ flex: 1, maxWidth: "200px" }}>
              <DatePicker
                value={renewalMonthYear}
                onChange={(date) => setRenewalMonthYear(date)}
                picker="month"
                format="MM/YYYY"
                placeholder="MM/YYYY"
                style={{ width: "100%" }}
                status={attemptedSubmit && !renewalMonthYear ? "error" : ""}
              />
              {attemptedSubmit && !renewalMonthYear && (
                <Typography
                  sx={{ color: "#E85858", fontSize: "12px", mt: 0.5 }}
                >
                  Renewal month and year is required
                </Typography>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "flex-start",
            }}
          >
            <FieldLabel required width="140px">
              Valid Till
            </FieldLabel>
            <Box sx={{ flex: 1, maxWidth: "200px" }}>
              <DatePicker
                value={validTillMonthYear}
                onChange={(date) => setValidTillMonthYear(date)}
                picker="month"
                format="MM/YYYY"
                placeholder="MM/YYYY"
                style={{ width: "100%" }}
                status={attemptedSubmit && !validTillMonthYear ? "error" : ""}
              />
              {attemptedSubmit && !validTillMonthYear && (
                <Typography
                  sx={{ color: "#E85858", fontSize: "12px", mt: 0.5 }}
                >
                  Valid till month and year is required
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      ) : (
        <FormField>
          <FieldLabel required>Valid Till</FieldLabel>
          <Box sx={{ flex: 1, maxWidth: "200px" }}>
            <DatePicker
              value={validTillMonthYear}
              onChange={(date) => setValidTillMonthYear(date)}
              picker="month"
              format="MM/YYYY"
              placeholder="MM/YYYY"
              disabled={!signupMonthYear || !validityYears}
              style={{ width: "100%" }}
              status={attemptedSubmit && !validTillMonthYear ? "error" : ""}
            />
            {attemptedSubmit && !validTillMonthYear && (
              <Typography sx={{ color: "#E85858", fontSize: "12px", mt: 0.5 }}>
                Valid till month and year is required
              </Typography>
            )}
          </Box>
        </FormField>
      )}

      <FormField>
        <FieldLabel>Invoice Date</FieldLabel>
        <Box sx={{ flex: 1, maxWidth: "400px" }}>
          <DatePicker
            value={invoiceDate}
            onChange={(date) => setInvoiceDate(date)}
            format="DD/MM/YYYY"
            placeholder="DD/MM/YYYY (optional)"
            style={{ width: "100%" }}
          />
        </Box>
      </FormField>

      <FormField>
        <FieldLabel>Invoice Document</FieldLabel>
        <Box sx={{ flex: 1, maxWidth: "500px" }}>
          <input
            id="invoice-upload"
            type="file"
            accept="application/pdf"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
          {!invoiceDocument ? (
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                textAlign: "center",
                cursor: "pointer",
                border: "2px dashed #D1D5DB",
                backgroundColor: "#F9FAFB",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "#F3F4F6",
                  borderColor: "#3B82F6",
                },
              }}
              onClick={() => document.getElementById("invoice-upload").click()}
            >
              <UploadIcon
                sx={{
                  fontSize: 48,
                  color: "#9CA3AF",
                  mb: 1,
                }}
              />
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#374151",
                  mb: 0.5,
                }}
              >
                Click to upload or drag and drop
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#6B7280",
                }}
              >
                PDF only (max. 10MB)
              </Typography>
            </Paper>
          ) : (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                display: "flex",
                alignItems: "center",
                gap: 2,
                border: "2px solid #10B981",
                backgroundColor: "#F0FDF4",
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 1,
                  backgroundColor: "#DBEAFE",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PdfIcon sx={{ fontSize: 28, color: "#2563EB" }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#1F2937",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {invoiceDocument.name}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#6B7280",
                    mt: 0.25,
                  }}
                >
                  {formatFileSize(invoiceDocument.size)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CheckCircleIcon sx={{ fontSize: 24, color: "#10B981" }} />
                <IconButton
                  size="small"
                  onClick={handleRemoveFile}
                  sx={{
                    color: "#EF4444",
                    "&:hover": {
                      backgroundColor: "#FEE2E2",
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            </Paper>
          )}
        </Box>
      </FormField>

      {/* Invoice Amount - Always visible, but empty in renewal mode */}
      <FormField>
        <FieldLabel required>Invoice Amount</FieldLabel>
        <TextField
          placeholder="Enter invoice amount"
          value={invoiceAmount}
          onChange={(e) => setInvoiceAmount(e.target.value)}
          size="small"
          sx={{ flex: 1, maxWidth: "400px" }}
          autoComplete="off"
          error={attemptedSubmit && !invoiceAmount.trim()}
          helperText={
            attemptedSubmit && !invoiceAmount.trim()
              ? "Invoice amount is required"
              : ""
          }
        />
      </FormField>

      {/* Invoice No. - Now optional */}
      <FormField>
        <FieldLabel>Invoice No.</FieldLabel>
        <TextField
          placeholder="Enter invoice number (optional)"
          value={invoiceNo}
          onChange={(e) => setInvoiceNo(e.target.value)}
          size="small"
          sx={{ flex: 1, maxWidth: "400px" }}
          autoComplete="off"
        />
      </FormField>
      {/* Renewal Activation */}
      {/* {membershipMode === "renewal" && (
        <FormField>
          <FieldLabel>Renewal Activation</FieldLabel>
          <RadioGroup
            row
            value={renewalActivation}
            onChange={(e) => setRenewalActivation(e.target.value)}
          >
            <FormControlLabel
              value="now"
              control={<Radio />}
              label="Now"
              disabled={false}
            />
            <FormControlLabel
              value="after_expiry"
              control={<Radio />}
              label="After Expiry"
              disabled={isExpired}
            />
   
          </RadioGroup>
        </FormField>
      )} */}

      {/* Status - Only shown in "New" mode */}
      {membershipMode === "new" && (
        <FormField>
          <FieldLabel>Status</FieldLabel>
          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography sx={{ fontSize: "13px", color: "#1F1F1F" }}>
                {isActive ? "Active" : "Inactive"}
              </Typography>
            }
            sx={{ ml: 0 }}
          />
        </FormField>
      )}
      {/* RM Details Section */}
        {![1, 2, 9, 13].includes(Number(membershipType?.membership_id)) && (
      <>
      <Divider sx={{ my: 4 }} />

        <Typography
          variant="h6"
          sx={{
            fontSize: "16px",
            fontWeight: 600,
            color: "#1F1F1F",
            mb: 3,
          }}
        >
          RM Details
        </Typography>

     <FormField>
      <FieldLabel required>RM Name</FieldLabel>
      <TextField
        placeholder="Enter RM name"
        value={rmName}
        onChange={(e) => setRmName(e.target.value)}
        size="small"
        sx={{ flex: 1, maxWidth: "400px" }}
        error={attemptedSubmit && !rmName.trim()}
        helperText={
          attemptedSubmit && !rmName.trim()
            ? "RM name is required"
            : ""
        }
      />
     </FormField>

    <FormField>
      <FieldLabel required>RM Email</FieldLabel>
       <TextField
          placeholder="Enter RM email"
          value={rmEmail}
          onChange={(e) => setRmEmail(e.target.value)}
          size="small"
          sx={{ flex: 1, maxWidth: "400px" }}
          error={
            attemptedSubmit &&
            (!rmEmail.trim() ||
              !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(rmEmail))
          }
          helperText={
            attemptedSubmit && !rmEmail.trim()
              ? "RM email is required"
              : attemptedSubmit &&
                !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(rmEmail)
              ? "Enter valid email"
              : ""
          }
        />
    </FormField>

    <FormField>
      <FieldLabel required>RM Mobile</FieldLabel>
      <TextField
        placeholder="Enter RM mobile number"
        value={rmMobile}
        onChange={(e) =>
          setRmMobile(e.target.value.replace(/\D/g, "").slice(0, 10))
        }
        size="small"
        sx={{ flex: 1, maxWidth: "400px" }}
        error={
          attemptedSubmit &&
          (!rmMobile.trim() || !/^[0-9]{10}$/.test(rmMobile))
        }
        helperText={
          attemptedSubmit && !rmMobile.trim()
            ? "RM mobile number is required"
            : attemptedSubmit &&
              !/^[0-9]{10}$/.test(rmMobile)
            ? "Enter valid 10 digit mobile number"
            : ""
        }
      />
    </FormField>
    </>
  )}

      <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
        <Button
          variant="contained"
          startIcon={
            isSubmitting ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <SaveIcon />
            )
          }
          onClick={handleSaveMembership}
          disabled={isSubmitting}
          sx={{
            backgroundColor: "#3B82F6",
            "&:hover": { backgroundColor: "#2563EB" },
            textTransform: "none",
            fontSize: "13px",
          }}
        >
          {isSubmitting
            ? `${membershipMode === "renewal" ? "Renewing" : "Saving"}...`
            : `${membershipMode === "renewal" ? "Renew" : "Save"} Membership`}
        </Button>
      </Box>

      {/* Dialogs and Modals */}
      <Dialog
        open={openMembershipDialog}
        onClose={() => {
          if (!isCreatingMembership) {
            setOpenMembershipDialog(false);
            setNewMembershipType("");
            setNewMembershipValidityYears(1);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: "16px", fontWeight: 600, pb: 2 }}>
          Create New Membership Plan
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Membership Plan Name"
            fullWidth
            variant="outlined"
            value={newMembershipType}
            onChange={(e) => setNewMembershipType(e.target.value)}
            placeholder="Enter new membership plan"
            disabled={isCreatingMembership}
            onKeyPress={(e) => {
              if (e.key === "Enter" && newMembershipType.trim()) {
                handleCreateNewMembershipType();
              }
            }}
            sx={{ mb: 2 }}
          />

          <TextField
            select
            fullWidth
            label="Years of Membership"
            value={newMembershipValidityYears}
            onChange={(e) => setNewMembershipValidityYears(e.target.value)}
            disabled={isCreatingMembership}
            variant="outlined"
          >
            {[...Array(10)].map((_, index) => {
              const year = index + 1;
              return (
                <MenuItem key={year} value={year}>
                  {year} {year === 1 ? "year" : "years"}
                </MenuItem>
              );
            })}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 2 }}>
          <Button
            onClick={() => {
              setOpenMembershipDialog(false);
              setNewMembershipType("");
              setNewMembershipValidityYears(1);
            }}
            sx={{ color: "#6B7280" }}
            disabled={isCreatingMembership}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateNewMembershipType}
            variant="contained"
            disabled={!newMembershipType.trim() || isCreatingMembership}
            startIcon={
              isCreatingMembership ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
            sx={{
              backgroundColor: "#3B82F6",
              "&:hover": { backgroundColor: "#2563EB" },
            }}
          >
            {isCreatingMembership ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <FeaturesModal
        open={featuresModal.open}
        membershipName={featuresModal.membershipName}
        membershipId={featuresModal.membershipId}
        features={featuresModal.features}
        onClose={handleCloseFeaturesModal}
        onOpenAddFeature={handleOpenAddFeatureModal}
        onFeaturesUpdated={handleFeaturesUpdated}
        setSnackbar={setSnackbar}
      />

      <AddFeatureModal
        open={addFeatureModal.open}
        membershipId={addFeatureModal.membershipId}
        existingFeatures={featuresModal.features}
        onClose={handleCloseAddFeatureModal}
        onFeaturesAdded={handleFeaturesUpdated}
        setSnackbar={setSnackbar}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MembershipDetails;
